import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { ComponentData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { Position } from "@/services/backend/htz/work-order/types/work-order";

/**
 * Applying an inspection change: a setter for the position plus the save status
 * of whoever owns the persistence. The consumer (an inspection tab) calls the
 * setter and reads the status flags to reflect saving/error state; it does not
 * persist directly. This used to be a full offline `Mutation`, but the position
 * is now persisted by the owning row's debounced save, so a plain setter is all
 * the inspection tabs need.
 */
export type OnPositionChange = [
  (position: Position) => void,
  {
    isLoading: boolean;
    error: unknown;
    isSuccess: boolean;
    reset: () => void;
  },
];

interface InspectionContextInterface {
  initialized: boolean;
  active: boolean;
  setActive: (value: boolean) => void;
  activeComponent: ComponentData | null;
  setActiveComponent: (c: ComponentData | null) => void;
  position: Position;
  onPositionChange: OnPositionChange;
  disabled?: boolean;
}

const InspectionContext = createContext<InspectionContextInterface>({
  initialized: false,
  active: false,
  setActive: () => null,
  activeComponent: null,
  setActiveComponent: () => null,
  position: {} as Position,
  onPositionChange: [
    () => {},
    {
      isLoading: false,
      error: undefined,
      isSuccess: false,
      reset: () => null,
    },
  ],
  disabled: false,
});

export function useInspectionContext() {
  return useContext(InspectionContext);
}

export function InspectionContextProvider({
  position,
  onPositionChange,
  disabled,
  children,
}: {
  position: Position;
  onPositionChange: OnPositionChange;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [active, setActive] = useState<boolean>(false);
  const [activeComponent, setActiveComponent] = useState<ComponentData | null>(
    null,
  );

  const value = useMemo(
    () => ({
      initialized: true,
      active,
      setActive,
      activeComponent,
      setActiveComponent,
      position,
      onPositionChange,
      disabled,
    }),
    [active, activeComponent, position, onPositionChange, disabled],
  );

  return (
    <InspectionContext.Provider value={value}>
      {children}
    </InspectionContext.Provider>
  );
}
